import { useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { colors } from 'constants/theme'
import { dateTimeToView } from 'utils/date.utils'
import { getPdfMake } from 'component/Pdf/pdfMakeThai'

const DropdownDocument = styled(Dropdown)(() => ({
    '.dropdown-toggle': {
        backgroundColor: `${colors.white} !important`,
        color: `${colors.themeSecondColor} !important`,
        border: `1px solid ${colors.themeSecondColor} !important`,
        display: 'flex',
        alignItems: 'center',
        boxShadow: 'none !important',
        height: 44,
        '&:after': { marginLeft: '0.5rem' },
        '&[aria-expanded="true"]:after': { transform: 'rotate(180deg)' },
    },
    '.dropdown-menu': {
        marginBottom: 4,
        borderRadius: 4,
        background: colors.white,
        border: `1px solid ${colors.lightGray}`,
        minWidth: '130px',
        '.dropdown-item': {
            '&:hover': {
                backgroundColor: colors.themeSecondColor06,
                color: colors.themeSecondColor,
            },
        },
    },
}))

type Props = {
    fetchRows: () => Promise<any[]>
    filters: string[]
    fileName?: string
    title?: string
}

type RenderContext = Omit<Props, 'fetchRows'> & { rows: any[] }

const buildColumns = (t: (k: string, opts?: any) => string) => [
    { key: 'no', header: '#', width: 6, pdfWidth: 20 as any },
    { key: 'name', header: t('PERSONNEL.TABLE.CELL.PERSONNEL_NAME'), width: 28, pdfWidth: '*' as any },
    { key: 'phone', header: t('PERSONNEL.TABLE.CELL.PERSONNEL_PHONE'), width: 16, pdfWidth: 60 as any },
    { key: 'position', header: t('PERSONNEL.TABLE.CELL.PERSONNEL_POSITION'), width: 22, pdfWidth: 80 as any },
    { key: 'role', header: t('PERSONNEL.TABLE.CELL.PERSONNEL_ROLE'), width: 22, pdfWidth: 70 as any },
    { key: 'branch', header: t('PERSONNEL.TABLE.CELL.PERSONNEL_BRANCH'), width: 22, pdfWidth: 80 as any },
    { key: 'status', header: t('USER.TABLE.CELL.STATUS'), width: 14, pdfWidth: 45 as any },
    { key: 'updatedBy', header: t('USER.TABLE.CELL.UPDATED_BY'), width: 24, pdfWidth: 80 as any },
]

const mapRow = (row: any, index: number, t: (k: string, opts?: any) => string) => {
    const firstname = row?.firstname || ''
    const lastname = row?.lastname || ''
    const adminSuffix = row?.isAdmin === '1' ? ` (${t('USER.TABLE.ADMIN')})` : ''
    const nameStr = `${firstname} ${lastname}`.trim() + adminSuffix
    const statusLabel = row?.status ? t(`STATUS.${row.status}`) : '-'
    const updatedAt = row?.updatedAt ? dateTimeToView(row.updatedAt, false) : ''
    const updatedBy = [row?.updatedBy || '-', updatedAt].filter(Boolean).join('\n')
    return {
        no: index + 1,
        name: nameStr || '-',
        phone: row?.phone || '-',
        position: row?.position?.positionName || '-',
        role: row?.roles?.roleName || '-',
        branch: row?.roles?.branchName || '-',
        status: statusLabel,
        updatedBy,
    }
}

const centerKeys = new Set(['no', 'status', 'updatedBy'])

async function exportExcel(props: RenderContext, t: (k: string, opts?: any) => string) {
    const cols = buildColumns(t)
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet(props.title || t('PERSONNEL.TITLE'), {
        pageSetup: { orientation: 'landscape', paperSize: 9 },
    })
    ws.properties.defaultRowHeight = 20
    ws.columns = cols.map((c) => ({ header: c.header, key: c.key, width: c.width })) as any

    let rowIdx = 1

    ws.mergeCells(rowIdx, 1, rowIdx, cols.length)
    ws.getCell(rowIdx, 1).value = props.title || t('PERSONNEL.TITLE')
    ws.getCell(rowIdx, 1).font = { size: 16, bold: true }
    ws.getCell(rowIdx, 1).alignment = { horizontal: 'center' }
    rowIdx++

    if (props.filters && props.filters.length) {
        ws.mergeCells(rowIdx, 1, rowIdx, cols.length)
        ws.getCell(rowIdx, 1).value = [...props.filters, `จำนวน: ${props.rows.length}`].join('    ')
        ws.getCell(rowIdx, 1).font = { size: 11 }
        ws.getCell(rowIdx, 1).alignment = { horizontal: 'left' }
        rowIdx += 2
    }

    const headerRow = ws.addRow(cols.map((c) => c.header))
    headerRow.height = 24
    headerRow.font = { bold: true }
    headerRow.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
    headerRow.eachCell((cell) => {
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        }
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF1F3F5' },
        }
    })

    if (!props.rows || props.rows.length === 0) {
        const empty = ws.addRow([t('NOT_FOUND')])
        ws.mergeCells(empty.number, 1, empty.number, cols.length)
        empty.alignment = { horizontal: 'center' }
        empty.font = { italic: true }
    } else {
        props.rows.forEach((row, i) => {
            const values = mapRow(row, i, t)
            const dataRow = ws.addRow(cols.map((c) => (values as any)[c.key]))
            dataRow.alignment = { vertical: 'middle', wrapText: true }
            dataRow.eachCell((cell, colNumber) => {
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' },
                }
                const colKey = cols[colNumber - 1].key
                if (centerKeys.has(colKey)) {
                    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
                }
            })
        })
    }

    const buf = await wb.xlsx.writeBuffer()
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    saveAs(blob, `${props.fileName || t('PERSONNEL.TITLE')}_${moment().format('YYYY-MM-DD')}.xlsx`)
}

async function exportPdf(props: RenderContext, t: (k: string, opts?: any) => string) {
    const pdfMake = await getPdfMake()
    const cols = buildColumns(t)

    const header = cols.map((c) => ({ text: c.header, style: 'tableHeader' }))

    const body: any[] = [header]
    if (!props.rows.length) {
        body.push([{ text: t('NOT_FOUND'), colSpan: cols.length, alignment: 'center', italics: true }])
    } else {
        props.rows.forEach((row, i) => {
            const v = mapRow(row, i, t)
            body.push(
                cols.map((c) => {
                    const val = (v as any)[c.key]
                    return {
                        text: val == null || val === '' ? '-' : String(val),
                        alignment: centerKeys.has(c.key) ? 'center' : 'left',
                    }
                }),
            )
        })
    }

    const filterLine = props.filters && props.filters.length
        ? [...props.filters, `จำนวน: ${props.rows.length}`].join('    ')
        : ''

    const docDefinition: any = {
        pageSize: 'A4',
        pageOrientation: 'landscape',
        pageMargins: [24, 40, 24, 40],
        defaultStyle: { font: 'Sarabun', fontSize: 9 },
        content: [
            { text: props.title || t('PERSONNEL.TITLE'), style: 'title' },
            filterLine ? { text: filterLine, style: 'filter' } : { text: '' },
            {
                table: {
                    headerRows: 1,
                    widths: cols.map((c) => c.pdfWidth),
                    body,
                },
                layout: {
                    fillColor: (rowIndex: number) => (rowIndex === 0 ? '#F1F3F5' : null),
                    hLineWidth: () => 0.5,
                    vLineWidth: () => 0.5,
                    hLineColor: () => '#B0BEC5',
                    vLineColor: () => '#B0BEC5',
                },
            },
        ],
        styles: {
            title: { fontSize: 16, bold: true, alignment: 'center', margin: [0, 0, 0, 6] },
            filter: { fontSize: 10, margin: [0, 0, 0, 8] },
            tableHeader: { bold: true, fontSize: 9, alignment: 'center' },
        },
        footer: (currentPage: number, pageCount: number) => ({
            text: `${currentPage} / ${pageCount}`,
            alignment: 'center',
            fontSize: 8,
            margin: [0, 12, 0, 0],
        }),
    }

    const fileName = `${props.fileName || t('PERSONNEL.TITLE')}_${moment().format('YYYY-MM-DD')}.pdf`
    pdfMake.createPdf(docDefinition).download(fileName)
}

export default function PersonnelsExportButton(props: Props) {
    const { t } = useTranslation()
    const [busy, setBusy] = useState(false)

    const withRows = async (): Promise<RenderContext> => {
        const rows = (await props.fetchRows()) || []
        const { fetchRows: _f, ...rest } = props
        return { ...rest, rows }
    }

    const handleExcel = async () => {
        if (busy) return
        setBusy(true)
        try {
            await exportExcel(await withRows(), t)
        } finally {
            setBusy(false)
        }
    }

    const handlePdf = async () => {
        if (busy) return
        setBusy(true)
        try {
            await exportPdf(await withRows(), t)
        } finally {
            setBusy(false)
        }
    }

    return (
        <DropdownDocument>
            <Dropdown.Toggle id="dropdown-personnels-export" className="ml-xl-auto" disabled={busy}>
                {t('PERSONNEL.BUTTON.PRINT_DOCUMENT')}
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Item onClick={handleExcel} disabled={busy}>
                    {t('Excel')}
                </Dropdown.Item>
                <Dropdown.Item onClick={handlePdf} disabled={busy}>
                    {t('PDF')}
                </Dropdown.Item>
            </Dropdown.Menu>
        </DropdownDocument>
    )
}
